/**
 * RP-2540: filters for Ticketing > Search.
 *
 * Vendor, Location, Ticket Name, one dropdown per modifier group ("Hotel",
 * "Date"...) and a date range. Every filter is multi-select; an empty
 * selection means "all".
 *
 * The filters run in two passes:
 *  1. buildFilterWhere() narrows the query on the server (Hasura), so the page
 *     isn't limited to the 1,000 most recent orders when a filter is set.
 *     Ticket name and modifier choices live inside the order's `items` jsonb,
 *     matched with `_contains`; that is "some line on the order has it".
 *  2. orderMatchesFilters() then checks each order line by line, so "Hotel
 *     Route 1" + "Hotel = Comfort Suites" only keeps orders where the SAME
 *     line has both (not one line with each).
 *
 * Lines store the name the ticket had when it was sold (`name`) and the
 * chosen options (`selected_modifiers: [{ name, label, price }]`).
 */

export interface TicketSearchFilters {
  vendorIds: string[];
  locationIds: string[];
  ticketNames: string[];
  /** modifier group name -> chosen option labels */
  modifiers: Record<string, string[]>;
  /** yyyy-mm-dd, local time; empty = open-ended */
  dateFrom: string;
  dateTo: string;
}

export const EMPTY_TICKET_FILTERS: TicketSearchFilters = {
  vendorIds: [],
  locationIds: [],
  ticketNames: [],
  modifiers: {},
  dateFrom: "",
  dateTo: "",
};

export interface FilterOption {
  value: string;
  label: string;
}

export interface ModifierGroupOptions {
  name: string;
  options: FilterOption[];
}

const clean = (s: unknown): string => (typeof s === "string" ? s.trim() : "");

const activeModifierEntries = (f: TicketSearchFilters) =>
  Object.entries(f.modifiers || {}).filter(([, labels]) => labels && labels.length > 0);

export function hasActiveFilters(f: TicketSearchFilters): boolean {
  return (
    f.vendorIds.length > 0 ||
    f.locationIds.length > 0 ||
    f.ticketNames.length > 0 ||
    activeModifierEntries(f).length > 0 ||
    !!f.dateFrom ||
    !!f.dateTo
  );
}

/** Start of a local yyyy-mm-dd day as an ISO string (UTC). */
export function localDayStartIso(day: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day || "");
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toISOString();
}

/** Start of the local day AFTER yyyy-mm-dd, so "to" includes the whole day. */
export function localNextDayStartIso(day: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day || "");
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 1).toISOString();
}

/** Hasura where-clause pieces for the filters (combined with _and by the caller). */
export function buildFilterWhere(f: TicketSearchFilters): any[] {
  const and: any[] = [];
  if (f.vendorIds.length) and.push({ vendor_id: { _in: f.vendorIds.map(Number) } });
  if (f.locationIds.length) and.push({ location_id: { _in: f.locationIds.map(Number) } });
  if (f.ticketNames.length) {
    and.push({ _or: f.ticketNames.map((name) => ({ items: { _contains: [{ name }] } })) });
  }
  for (const [group, labels] of activeModifierEntries(f)) {
    and.push({
      _or: labels.map((label) => ({
        items: { _contains: [{ selected_modifiers: [{ name: group, label }] }] },
      })),
    });
  }
  const from = localDayStartIso(f.dateFrom);
  const to = localNextDayStartIso(f.dateTo);
  if (from || to) {
    and.push({ created_at: { ...(from ? { _gte: from } : {}), ...(to ? { _lt: to } : {}) } });
  }
  return and;
}

const linesOf = (items: unknown): any[] => (Array.isArray(items) ? items : []);

const chosenLabel = (line: any, group: string): string | null => {
  const sel = Array.isArray(line?.selected_modifiers) ? line.selected_modifiers : [];
  const hit = sel.find((m: any) => clean(m?.name) === group);
  return hit ? clean(hit.label) : null;
};

/** True when this one order line satisfies the ticket-name and modifier filters. */
export function lineMatchesFilters(line: any, f: TicketSearchFilters): boolean {
  if (f.ticketNames.length && !f.ticketNames.includes(clean(line?.name))) return false;
  for (const [group, labels] of activeModifierEntries(f)) {
    const label = chosenLabel(line, group);
    if (!label || !labels.includes(label)) return false;
  }
  return true;
}

/** Client-side check of a loaded order against every filter. */
export function orderMatchesFilters(order: any, f: TicketSearchFilters): boolean {
  if (f.vendorIds.length && !f.vendorIds.includes(String(order?.vendor_id ?? ""))) return false;
  if (f.locationIds.length && !f.locationIds.includes(String(order?.location_id ?? ""))) return false;
  const from = localDayStartIso(f.dateFrom);
  const to = localNextDayStartIso(f.dateTo);
  if (from || to) {
    const t = order?.created_at ? new Date(order.created_at).getTime() : NaN;
    if (!Number.isFinite(t)) return false;
    if (from && t < new Date(from).getTime()) return false;
    if (to && t >= new Date(to).getTime()) return false;
  }
  if (f.ticketNames.length || activeModifierEntries(f).length) {
    return linesOf(order?.items).some((line) => lineMatchesFilters(line, f));
  }
  return true;
}

/**
 * "2 × Hotel Route 2 (Hotel: Courtyard · Date: Saturday - 11/7/26)" per line,
 * one line per ticket on the order. With filters set, only the matching lines.
 */
export function ticketDetailLines(items: unknown, f?: TicketSearchFilters): string[] {
  const filterLines = !!f && (f.ticketNames.length > 0 || activeModifierEntries(f).length > 0);
  return linesOf(items)
    .filter((line) => !filterLines || lineMatchesFilters(line, f!))
    .map((line) => {
      const qty = Number(line?.quantity) || 1;
      const name = clean(line?.name) || clean(line?.variant_name) || "Ticket";
      const sel = Array.isArray(line?.selected_modifiers) ? line.selected_modifiers : [];
      const mods = sel
        .map((m: any) => (clean(m?.name) && clean(m?.label) ? `${clean(m.name)}: ${clean(m.label)}` : ""))
        .filter(Boolean);
      return `${qty} × ${name}${mods.length ? ` (${mods.join(" · ")})` : ""}`;
    });
}

/** Total ticket count on the order (matching lines only, when filtered). */
export function ticketQuantity(items: unknown, f?: TicketSearchFilters): number {
  const filterLines = !!f && (f.ticketNames.length > 0 || activeModifierEntries(f).length > 0);
  return linesOf(items)
    .filter((line) => !filterLines || lineMatchesFilters(line, f!))
    .reduce((n, line) => n + (Number(line?.quantity) || 1), 0);
}

const sortOptions = (opts: FilterOption[]) =>
  opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

/** Ticket names from the event's ticketing items plus any seen on loaded orders. */
export function buildTicketNameOptions(itemDefs: any[], orders: any[] = []): FilterOption[] {
  const names = new Set<string>();
  itemDefs.forEach((i) => clean(i?.name) && names.add(clean(i.name)));
  orders.forEach((o) => linesOf(o?.items).forEach((l) => clean(l?.name) && names.add(clean(l.name))));
  return sortOptions([...names].map((n) => ({ value: n, label: n })));
}

/**
 * One option list per modifier group name, from the ticketing items'
 * definitions plus choices seen on loaded orders (covers renamed options).
 * When ticket names are picked, only those tickets' groups are offered.
 */
export function buildModifierGroupOptions(
  itemDefs: any[],
  orders: any[] = [],
  ticketNames: string[] = []
): ModifierGroupOptions[] {
  const pick = (name: string) => !ticketNames.length || ticketNames.includes(name);
  const groups = new Map<string, Set<string>>();
  const add = (group: string, label: string) => {
    if (!group || !label) return;
    if (!groups.has(group)) groups.set(group, new Set());
    groups.get(group)!.add(label);
  };
  itemDefs.forEach((item) => {
    if (!pick(clean(item?.name))) return;
    let mods = item?.modifiers;
    if (typeof mods === "string") {
      try { mods = JSON.parse(mods); } catch { mods = []; }
    }
    (Array.isArray(mods) ? mods : []).forEach((g: any) =>
      (Array.isArray(g?.options) ? g.options : []).forEach((o: any) => add(clean(g?.name), clean(o?.label)))
    );
  });
  orders.forEach((o) =>
    linesOf(o?.items).forEach((line) => {
      if (!pick(clean(line?.name))) return;
      (Array.isArray(line?.selected_modifiers) ? line.selected_modifiers : []).forEach((m: any) =>
        add(clean(m?.name), clean(m?.label))
      );
    })
  );
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, labels]) => ({
      name,
      options: sortOptions([...labels].map((l) => ({ value: l, label: l }))),
    }));
}

/** Distinct id/name options, e.g. from orders_ticketing(distinct_on: vendor_id). */
export function buildIdNameOptions(rows: any[], idKey: string, relKey: string): FilterOption[] {
  const seen = new Map<string, string>();
  rows.forEach((r) => {
    const id = r?.[idKey];
    if (id === null || id === undefined) return;
    seen.set(String(id), clean(r?.[relKey]?.name) || `#${id}`);
  });
  return sortOptions([...seen.entries()].map(([value, label]) => ({ value, label })));
}

/** CheckboxDropdown reports "All" as every value; treat that as no filter. */
export function normalizeSelection(selected: string[] | null, options: FilterOption[]): string[] {
  if (!selected || selected.length === 0) return [];
  if (options.length > 0 && selected.length >= options.length) {
    const all = options.every((o) => selected.includes(o.value));
    if (all) return [];
  }
  return selected;
}

const escapeHtml = (s: unknown) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * A plain printable check-in sheet: a blank check box, then the chosen
 * columns. Cell values may hold several lines (one per ticket).
 */
export function buildPrintHtml(
  title: string,
  subtitle: string,
  headers: string[],
  rows: string[][]
): string {
  const head = ["✓", ...headers].map((h) => `<th>${escapeHtml(h)}</th>`).join("");
  const body = rows
    .map(
      (r) =>
        `<tr><td class="box"></td>${r
          .map((c) => `<td>${escapeHtml(c).replace(/\n/g, "<br>")}</td>`)
          .join("")}</tr>`
    )
    .join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; margin: 16px; color: #111; }
  h1 { font-size: 16px; margin: 0 0 2px; }
  p { margin: 0 0 10px; color: #444; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #999; padding: 4px 6px; text-align: left; vertical-align: top; }
  th { background: #eee; }
  tr { page-break-inside: avoid; }
  td.box { width: 18px; }
  @page { margin: 12mm; }
</style></head><body>
<h1>${escapeHtml(title)}</h1><p>${escapeHtml(subtitle)}</p>
<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
</body></html>`;
}
